import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, TrendingUp, Brain, Target, Users, Laptop, Heart, Award, Coffee, Zap, Play, ChevronRight } from 'lucide-react';

import testimonials from '../../assets/careers/testimonials.png';
import explore from '../../assets/careers/explore.png';
import join from '../../assets/careers/join.png';
import learn1 from '../../assets/careers/learn1.png';
import learn2 from '../../assets/careers/learn2.png';
import learn3 from '../../assets/careers/learn3.png';
import learn4 from '../../assets/careers/learn4.png';

const ModernBenefitsCultureSection = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [hoveredBenefit, setHoveredBenefit] = useState(null);
    const [hoveredCulture, setHoveredCulture] = useState(null);
    const [activeVideo, setActiveVideo] = useState(null);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.1 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    const benefits = [
        {
            id: 1,
            title: 'Empowering Employees with Growth Opportunities and Work-Life Balance',
            description: 'At RoadExp, we prioritize your career with attractive opportunities and work-life balance solutions.',
            icon: TrendingUp,
            color: '#3B82F6',
            image: testimonials,
            features: [
                'Career advancement programs',
                'Flexible working hours',
                'Professional development budget',
                'Mentorship opportunities'
            ]
        },
        {
            id: 2,
            title: 'Incentives That Reward Your Hard Work and Dedication',
            description: 'Our incentive programs are designed to recognize and reward exceptional performance and commitment.',
            icon: Award,
            color: '#10B981',
            image: explore,
            features: [
                'Performance-based bonuses',
                'Recognition programs',
                'Stock option plans',
                'Achievement awards'
            ]
        },
        {
            id: 3,
            title: 'Career Growth Paths Tailored to Your Ambitions and Goals',
            description: 'We help to advance your career with clear advancement growth opportunities and support.',
            icon: Target,
            color: '#F59E0B',
            image: join,
            features: [
                'Individual development plans',
                'Leadership training programs',
                'Cross-functional opportunities',
                'Skills enhancement workshops'
            ]
        }
    ];

    const cultureHighlights = [
        {
            id: 1,
            title: 'Create your RoadExpress careers profile',
            description: 'Build your professional profile and showcase your skills to join our innovative team.',
            image: learn1,
            category: 'Getting Started'
        },
        {
            id: 2,
            title: 'The disability community at Road Express',
            description: 'Celebrating diversity and inclusion in our workplace culture for all team members.',
            image: learn2,
            category: 'Diversity & Inclusion'
        },
        {
            id: 3,
            title: 'Take a look inside our hiring process at Road Express',
            description: 'Transparent and fair hiring practices that focus on talent and potential.',
            image: learn3,
            category: 'Hiring Process'
        },
        {
            id: 4,
            title: 'Veterans and their families make great Road Express',
            description: 'Supporting our veteran community with specialized programs and opportunities.',
            image: learn4,
            category: 'Veterans Program'
        }
    ];

    const handleVideoPlay = (videoId) => {
        setActiveVideo(videoId);
        console.log(`Playing video: ${videoId}`);
    };

    return (
        <section
            ref={sectionRef}
            className="w-full bg-white relative overflow-hidden"
        >
            {/* Benefits Section */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                <div className="mb-16">
                    <div
                        className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                            }`}
                    >
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-px bg-[#F05A28] mr-4"></div>
                            <span className="text-[#F05A28] font-semibold tracking-wider text-sm uppercase flex items-center">
                                <Zap className="w-4 h-4 mr-2" />
                                Employee Benefits
                            </span>
                        </div>

                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                            Discover Competitive
                            <br />
                            <span className="text-[#F05A28] italic">Salaries and Exceptional Benefits</span>
                        </h2>

                        <p className="text-xl text-gray-600 leading-relaxed max-w-3xl">
                            At RoadExpress, we believe in rewarding our team with comprehensive benefits,
                            competitive compensation, and opportunities for professional growth.
                        </p>
                    </div>
                </div>

                {/* Benefits Grid */}
                <div className="grid md:grid-cols-3 gap-8 mb-10">
                    {benefits.map((benefit, index) => {
                        const isHovered = hoveredBenefit === benefit.id;

                        return (
                            <div
                                key={benefit.id}
                                className={`group relative bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-500 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'
                                    }`}
                                style={{ transitionDelay: `${0.4 + index * 0.2}s` }}
                                onMouseEnter={() => setHoveredBenefit(benefit.id)}
                                onMouseLeave={() => setHoveredBenefit(null)}
                            >
                                {/* Image Container */}
                                <div className="relative h-48 overflow-hidden">
                                    <img
                                        src={benefit.image}
                                        alt={benefit.title}
                                        className="w-full h-full object-cover"
                                    />

                                    {/* Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                                </div>

                                {/* Content */}
                                <div className="p-8">
                                    <h3 className="text-xl font-bold text-gray-900 mb-4 line-height-tight">
                                        {benefit.title}
                                    </h3>

                                    <p className="text-gray-600 leading-relaxed">
                                        {benefit.description}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Culture Section */}
            <div className="bg-gray-50 py-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div
                        className={`text-center mb-16 transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                            }`}
                        style={{ transitionDelay: '0.8s' }}
                    >
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                            Working at <span className="text-[#F05A28] italic">Road Express</span>
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Discover what makes our workplace special through the eyes of our team members
                            and learn about our commitment to creating an inclusive, innovative environment.
                        </p>
                    </div>

                    {/* Culture Videos Grid */}
                    <div className="grid md:grid-cols-2 gap-8">
                        {cultureHighlights.map((item, index) => (
                            <div
                                key={item.id}
                                className={`group relative bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 cursor-pointer transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-16 opacity-0'
                                    }`}
                                style={{ transitionDelay: `${1 + index * 0.15}s` }}
                                onMouseEnter={() => setHoveredCulture(item.id)}
                                onMouseLeave={() => setHoveredCulture(null)}
                                onClick={() => handleVideoPlay(item.id)}
                            >
                                {/* Video Thumbnail */}
                                <div className="relative h-64 overflow-hidden">
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        className="w-full h-full object-cover"
                                    />

                                    {/* Play Button Overlay */}
                                    {/* <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300">
                                            <Play className="w-8 h-8 text-[#F05A28] ml-1" />
                                        </div>
                                    </div> */}

                                    {/* Category Badge */}
                                    <div className="absolute top-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full">
                                        <span className="text-xs font-semibold text-gray-800">{item.category}</span>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-8">
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-gray-800 transition-colors">
                                        {item.title}
                                    </h3>

                                    <p className="text-gray-600 leading-relaxed mb-4">
                                        {item.description}
                                    </p>
                                </div>

                                {/* Accent line */}
                                <div className={`h-1 bg-[#F05A28] rounded-b-3xl transition-all duration-500 origin-left ${hoveredCulture === item.id ? 'scale-x-100' : 'scale-x-0'
                                    }`}></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ModernBenefitsCultureSection;